import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getEventStatus } from "../../api/eventStatusAPI";
import { getNode } from "../../api/nodeAPI";
import ConvertOIDValueToString from "../../utils/objectConverter";
import { useRefreshInterval } from "../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { useObjectData } from "../../hooks/useLocalStorageData";

// RCU: each device can have up to 3 sensors. The sensor type (level/temp/IO) is
// unknown, but the per-sensor *status* event codes are fixed. We aggregate the
// status of every device's sensor N into the Sensor Status summary grid.
const SENSORS = [
  { idx: 1, statusCode: 3018 },
  { idx: 2, statusCode: 3019 },
  { idx: 3, statusCode: 3020 },
];
const STATUS_CODES = SENSORS.map((s) => s.statusCode);

// Same color mapping as WaterLevelCylinders' statusVisual (detail page).
// Level Sensor status enum: 6 NORMAL | -4 PASİF | -10 SENSÖR HATASI |
// -109 LOW | -110 VERY LOW | -111 HIGH | -112 VERY HIGH
function statusVisual(rawStatus, alarmType) {
  const v = Number(rawStatus);
  const RED = "#d32f2f"; // alarm
  const ORANGE = "#ed6c02"; // warning
  const BLUE = "#1976d2"; // normal
  const GRAY = "#9e9e9e"; // passive

  if (v === -4) return GRAY; // passive
  if (alarmType === 2 || v === -10 || v === -110 || v === -112) return RED;
  if (alarmType === 1 || v === -109 || v === -111) return ORANGE;
  return BLUE; // normal
}

// "Details" from the event/alarm history: the event's diagnostics formatted as
// "name = value" (e.g. "Water Level = 74"), same as the history description.
// getEventStatus delivers diagnosticList as a JSON STRING, e.g.
//   "[{\"oid\":3009,\"val\":74}]"  — so it must be parsed first.
function computeDetails(diagnosticList, od) {
  let list = diagnosticList;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch (e) {
      return "-";
    }
  }
  if (!Array.isArray(list) || list.length === 0) return "-";
  const parts = list
    .filter((d) => d.oid !== 206 && d.oid !== 1001)
    .map((d) => {
      const o = od.find((x) => x.idObject === d.oid);
      if (o) {
        return `${o.name} = ${ConvertOIDValueToString({ Type: o.type }, d.val)}`;
      }
      return d.val != null ? `${d.oid} = ${d.val}` : null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(" | ") : "-";
}

export default function SensorStatusPieCharts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { interval, isRunning } = useRefreshInterval();
  const isPageVisible = usePageVisibility();
  const objectData = useObjectData();
  // Aggregated across all sensors: [{id, label, count, alarmType, statusColor, devices}]
  const [summaryRows, setSummaryRows] = useState([]);
  // Labels (row ids) of the rows whose device tables are expanded (multi-select).
  const [selectedIds, setSelectedIds] = useState([]);
  const isFetchingRef = useRef(false);
  const objectDataRef = useRef(objectData);
  objectDataRef.current = objectData;

  useEffect(() => {
    let intervalId;

    async function fetchStatuses() {
      if (!isPageVisible || isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const [result, nodesResult] = await Promise.all([
          getEventStatus(),
          getNode(),
        ]);
        const list = Array.isArray(result) ? result : result?.data;
        if (!Array.isArray(list)) return;
        const nodes = Array.isArray(nodesResult)
          ? nodesResult
          : nodesResult?.data || [];
        const nodeNameById = new Map(nodes.map((n) => [n.idNode, n.name]));

        const od = objectDataRef.current || [];
        const typeByCode = {};
        STATUS_CODES.forEach((c) => {
          typeByCode[c] = od.find((o) => o.code === c)?.type;
        });

        // Keep the most severe status per (node, sensor) so a device counts once.
        const byKey = new Map();
        list.forEach((item) => {
          if (!STATUS_CODES.includes(item.event)) return;
          const key = `${item.nodeId}|${item.event}`;
          const prev = byKey.get(key);
          if (prev == null || item.alarmType > prev.alarmType) {
            byKey.set(key, {
              nodeId: item.nodeId,
              event: item.event,
              value: item.value,
              alarmType: item.alarmType,
              diagnosticList: item.diagnosticList,
            });
          }
        });

        const idxByCode = {};
        SENSORS.forEach((s) => (idxByCode[s.statusCode] = s.idx));

        // Group by the converted status label (same text as the detail chip).
        // Each group also tracks the contributing devices (node + sensor).
        const groupsByCode = {};
        STATUS_CODES.forEach((c) => (groupsByCode[c] = new Map()));
        byKey.forEach(({ nodeId, event, value, alarmType, diagnosticList }) => {
          let label;
          try {
            label = ConvertOIDValueToString({ Type: typeByCode[event] }, value);
          } catch (e) {
            label = String(value);
          }
          if (label == null || label === "") label = String(value);

          const map = groupsByCode[event];
          const device = {
            nodeId,
            sensorIdx: idxByCode[event],
            value: label,
            details: computeDetails(diagnosticList, od),
          };
          const existing = map.get(label);
          if (existing) {
            existing.value += 1;
            existing.devices.push(device);
          } else {
            map.set(label, {
              label,
              value: 1,
              color: statusVisual(value, alarmType),
              alarmType,
              devices: [device],
            });
          }
        });

        // Aggregate across all sensors by status label for the summary grid.
        const combined = new Map();
        STATUS_CODES.forEach((c) => {
          groupsByCode[c].forEach((g) => {
            const ex = combined.get(g.label);
            if (ex) {
              ex.count += g.value;
              ex.devices.push(...g.devices);
            } else {
              combined.set(g.label, {
                id: g.label,
                label: g.label,
                count: g.value,
                alarmType: g.alarmType,
                statusColor: g.color,
                devices: [...g.devices],
              });
            }
          });
        });
        const summary = Array.from(combined.values())
          .map((r) => ({
            ...r,
            devices: r.devices.map((d) => ({
              ...d,
              name: nodeNameById.get(d.nodeId) || `Node ${d.nodeId}`,
            })),
          }))
          .sort(
            (a, b) =>
              b.alarmType - a.alarmType ||
              b.count - a.count ||
              a.label.localeCompare(b.label)
          );
        setSummaryRows(summary);
      } catch (e) {
        console.error("SensorStatus fetch failed:", e);
      } finally {
        isFetchingRef.current = false;
      }
    }

    fetchStatuses();
    if (isRunning && isPageVisible) {
      const ms = Math.max((interval || 30) * 1000, 5000);
      intervalId = setInterval(fetchStatuses, ms);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [interval, isRunning, isPageVisible]);

  // No default selection: all rows start collapsed/unselected.

  const statusText = (alarmType) =>
    alarmType >= 2
      ? t("dashboard.sensorStatus.statusAlarm", "Alarm")
      : alarmType === 1
      ? t("dashboard.sensorStatus.warning", "Warning")
      : t("dashboard.sensorStatus.normal", "Normal");

  const columns = useMemo(
    () => [
      {
        field: "status",
        headerName: t("dashboard.sensorStatus.status", "Status"),
        flex: 1,
        minWidth: 130,
        align: "left",
        headerAlign: "left",
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: params.row.statusColor,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2">
              {statusText(params.row.alarmType)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "label",
        headerName: t("dashboard.sensorStatus.value", "Value"),
        flex: 1.5,
        minWidth: 160,
        align: "left",
        headerAlign: "left",
        sortable: false,
      },
      {
        field: "count",
        headerName: t("dashboard.sensorStatus.count", "Count"),
        flex: 0.6,
        minWidth: 90,
        align: "left",
        headerAlign: "left",
        sortable: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: "divider",
        borderRadius: 0,
        borderWidth: 1.5,
        mb: 2,
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: "1.15rem", mb: 1.5, textAlign: "center" }}
        >
          {t("dashboard.sensorStatus.sensorStatusTitle", "Alarms")}
        </Typography>

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={summaryRows}
            columns={columns}
            autoHeight
            hideFooter
            density="compact"
            checkboxSelection
            disableColumnMenu
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(model) => setSelectedIds(model)}
            localeText={{
              noRowsLabel: t("dashboard.sensorStatus.noData", "No data"),
            }}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />

          <Collapse in={selectedIds.length > 0} unmountOnExit>
            {/* Slave table: one combined list for all selected statuses, indented
                to the right with a left border so it reads as a sub-table. */}
            <Box
              sx={{
                mt: 2,
                ml: { xs: 2, sm: 4 },
                pl: 2,
                borderLeft: "2px solid",
                borderColor: "divider",
              }}
            >
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    "& td, & th": { py: 0.5, px: 1 },
                    "& th": { fontWeight: 700 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("dashboard.sensorStatus.device", "Device")}
                      </TableCell>
                      <TableCell>
                        {t("dashboard.sensorStatus.sensor", "Sensor")}
                      </TableCell>
                      <TableCell>
                        {t("dashboard.sensorStatus.value", "Value")}
                      </TableCell>
                      <TableCell>
                        {t("dashboard.sensorStatus.details", "Details")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryRows
                      .filter((r) => selectedIds.includes(r.id))
                      .flatMap((r) =>
                        r.devices.map((d) => ({
                          ...d,
                          statusColor: r.statusColor,
                        }))
                      )
                      .map((dvc, i) => (
                        <TableRow
                          key={`${dvc.nodeId}-${dvc.sensorIdx}-${i}`}
                          hover
                          onClick={() => navigate(`/places/${dvc.nodeId}`)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{dvc.name}</TableCell>
                          <TableCell>{dvc.sensorIdx}</TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              sx={{ alignItems: "center", gap: 1 }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: dvc.statusColor,
                                  flexShrink: 0,
                                }}
                              />
                              {dvc.value}
                            </Stack>
                          </TableCell>
                          <TableCell>{dvc.details}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  );
}
